import { del, get, keys, set } from 'idb-keyval'
import { StateRowTransportCollection, StateRowTransportValue } from '../../../backend.d'
import { ArmyPaint } from '../ArmyPainter/ArmyPaint.type'
import { ProfileId } from '../ProfileId.type'
import randomStateIdValue from '../state/randomStateIdValue'
import { State } from '../state/State.type'
import { encodePersistedStateBytes } from '../state/persisted_state'
import { StateOnlineId } from '../state/StateOnlineId.type'

const STORE_NAME_PREFIX = 'st1_'

export class IndexedDBDisk {
  private prefix: string

  constructor(type: string) {
    this.prefix = `${STORE_NAME_PREFIX}${type}_`
  }

  async save(state: State): Promise<StateRowTransportValue> {
    let id = state.map.id
    if (!id) {
      // Generate a unique ID within the valid range [1000000, 2147483647]
      id = await this.generateUniqueId()
      state.map.id = id as StateOnlineId
    }

    const now = new Date().toISOString()
    const user = { id: 0 as ProfileId, handle: 'Local', dname: 'Local Player', avatar_id: null }

    const row: StateRowTransportValue = {
      id: id as StateOnlineId,
      data: encodePersistedStateBytes(state),
      action_logs: [],
      created_at: now,
      updated_at: now,
      published_at: null,
      deleted_at: null,
      creator: user,
      owner: user,
      turn: null
    }

    console.log('saving', `${this.prefix}${id}`)
    await set(`${this.prefix}${id}`, row)
    return row
  }

  private async generateUniqueId(): Promise<number> {
    const maxAttempts = 100
    for (let i = 0; i < maxAttempts; i++) {
      const id = randomStateIdValue()
      const existing = await get(`${this.prefix}${id}`)
      if (!existing) {
        return id
      }
    }
    throw new Error('Failed to generate unique ID after multiple attempts')
  }

  async load(id: number | string): Promise<StateRowTransportValue | null> {
    console.log('loading', `${this.prefix}${id}`)
    return (await get(`${this.prefix}${id}`)) || null
  }

  async list(): Promise<StateRowTransportCollection> {
    const allKeys = await keys()
    const relevantKeys = allKeys.filter(k => typeof k === 'string' && k.startsWith(this.prefix))
    const results: StateRowTransportCollection = []
    for (const key of relevantKeys) {
      const val = await get(key)
      if (val) results.push(val as StateRowTransportValue)
    }
    return results
  }

  async delete(id: number | string): Promise<void> {
    await del(`${this.prefix}${id}`)
  }
}

export const gameDisk = new IndexedDBDisk('game')
export const mapDisk = new IndexedDBDisk('map')
export const lobbyDisk = new IndexedDBDisk('lobby')
export const replayDisk = new IndexedDBDisk('replay')

export class ArmyPaintDisk {
  private prefix = 'st1_army_paint_'

  async save(ap: ArmyPaint): Promise<void> {
    await set(`${this.prefix}${ap.id}`, ap)
  }

  async load(id: string | number): Promise<ArmyPaint | null> {
    return (await get(`${this.prefix}${id}`)) || null
  }

  async list(): Promise<Array<ArmyPaint>> {
    const allKeys = await keys()
    const relevantKeys = allKeys.filter(k => typeof k === 'string' && k.startsWith(this.prefix))
    const results: ArmyPaint[] = []
    for (const key of relevantKeys) {
      const val = await get(key)
      if (val) results.push(val)
    }
    return results
  }

  async delete(id: string | number): Promise<void> {
    await del(`${this.prefix}${id}`)
  }
}

export const armyPaintDisk = new ArmyPaintDisk()
